import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { CreateSavedMealResponse, SavedMeal } from '@calorie/shared';
import { haptic, hapticResult } from '../lib/telegram.js';

interface Props {
  meals: SavedMeal[];
  /** Тап по блюду - добавить его в дневник. */
  onAdd: (meal: SavedMeal) => void;
  onDelete: (id: string) => void;
  /** Разобрать состав и сохранить. Возвращает результат, чтобы показать промахи. */
  onCreate: (title: string, text: string) => Promise<CreateSavedMealResponse>;
  onClose: () => void;
}


export function MealsSheet({ meals, onAdd, onDelete, onCreate, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  // Esc закрывает - удобно при отладке в браузере
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Даём анимации доиграть, прежде чем размонтировать. */
  function close() {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 200);
  }

  function add(meal: SavedMeal) {
    haptic('medium');
    onAdd(meal);
    close();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    const body = text.trim();
    if (t.length < 1 || body.length < 2 || creating) return;

    setCreating(true);
    setNote(null);
    try {
      const res = await onCreate(t, body);
      if (res.meal) {
        hapticResult('success');
        setTitle('');
        setText('');
        if (res.unresolved.length > 0) {
          setNote(`Сохранил, но не разобрал: ${res.unresolved.map((u) => u.text).join(', ')}`);
        }
        titleRef.current?.focus();
      } else {
        hapticResult('warning');
        setNote('Не смог разобрать состав. Попробуйте проще - «овсянка 100 г, банан».');
      }
    } catch {
      hapticResult('error');
      setNote('Не получилось сохранить. Проверьте соединение.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className={`sheet-backdrop${closing ? ' closing' : ''}`}
      onClick={close}
      role="presentation"
    >
      <div
        className={`sheet glass${closing ? ' closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Мои блюда"
      >
        <div className="sheet-grabber" />
        <div className="sheet-head">
          <span className="sheet-title">Мои блюда</span>
          <button className="icon-btn" onClick={close} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <div className="sheet-body">
          {meals.length === 0 ? (
            <p className="sheet-empty">
              Пока пусто. Добавьте блюдо, которое едите часто, - и дальше оно
              будет попадать в дневник одним нажатием.
            </p>
          ) : (
            <ul className="meal-list">
              {meals.map((meal) => (
                <li className="meal-row" key={meal.id}>
                  <button className="meal-add" onClick={() => add(meal)}>
                    <span className="meal-name">{meal.title}</span>
                    <span className="meal-macros">
                      {Math.round(meal.kcal)} ккал
                      <i className="meal-dot" />Б {meal.protein}
                      <i className="meal-dot" />Ж {meal.fat}
                      <i className="meal-dot" />У {meal.carbs}
                    </span>
                  </button>
                  <button
                    className="meal-remove"
                    aria-label={`Удалить ${meal.title}`}
                    onClick={() => {
                      haptic('light');
                      onDelete(meal.id);
                    }}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form className="meal-new" onSubmit={submit}>
            <span className="meal-new-caption">Новое блюдо</span>
            <input
              ref={titleRef}
              className="meal-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Название - например «Мой завтрак»"
              maxLength={80}
              disabled={creating}
            />
            <input
              className="meal-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Что внутри - «овсянка 60 г, банан, кофе с молоком»"
              maxLength={500}
              disabled={creating}
            />
            {note && <p className="meal-note">{note}</p>}
            <button
              className="btn"
              type="submit"
              disabled={creating || title.trim().length < 1 || text.trim().length < 2}
            >
              {creating ? <span className="composer-spinner" /> : 'Разобрать и сохранить'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
